/**
 * Overview tab — the default landing view for an account.
 *
 * Surfaces an "Account Summary" card with the at-a-glance company and lifecycle
 * facts. Admins can edit the summary fields (account name, DBA, members, state,
 * expected effective). In the client portal the card locks once the account is
 * sent to CBS for implementation ("Submitted for implementation" onward) —
 * CBS owns the record from that point. The internal portal stays editable
 * because CBS staff are the ones who own it after the hand-off.
 */

import React from 'react';
import {AlertTriangle, FileText, Lock, Pencil, Wand2} from 'lucide-react';
import {format} from 'date-fns';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {Input} from '@common-web/components/ui/input';
import type {LifecyclePhase, AccountsSubStage} from '@api/generated/client';
import {
  PHASE_LABEL,
  SUB_STAGE_LABEL,
  toActiveSubStage,
} from '@common-web/components/account/phaseLabels';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  findPositionBySystemSubStage,
  isAccountSentToCbs,
  resolveAccountPosition,
} from '@common/accountsView';
import type {UsStateCode} from '@common/constants/usStates';
import {CODE_PERMISSIONS} from '@common/permissions';
import {usePermissions} from '@common-web/hooks/use-permissions';
import {trpc, type RouterInput} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {ProfileCard, ProfileRow, ProfileSubheading} from '@common-web/components/profile-card';
import {StateSelect} from '@client/components/StateSelect';
import {StageTooltip} from '../components/StageTooltip';
import {ProposalRow} from '@common-web/components/proposals/ProposalRow';

/** Plan Year & Coverage facts from the linked Group's metadata (set by the
 *  CBS build wizard / champ admin portal; null until then). */
interface GroupCoverageMetadata {
  planYearStartDate: Date | string | null;
  planYearEndDate: Date | string | null;
  planYearAnticipatedDate: Date | string | null;
  openEnrollmentStartDate: Date | string | null;
  openEnrollmentEndDate: Date | string | null;
  ppoNetwork: string | null;
  pbm: string | null;
  carrier: string | null;
  enroller: string | null;
}

/** Coverage vendor selections from the RFC's internal-use section
 *  (RfcInternalData) — the pre-group source for the Plan Year & Coverage card. */
interface RfcCoverageInternalData {
  carrierName?: string;
  carrierNameOther?: string;
  telemedicine?: string;
  telemedicineOther?: string;
  pbm?: string;
  pbmOther?: string;
  app?: string;
  appOther?: string;
  mec?: string;
  mecOther?: string;
  ppoNetwork?: string;
  ppoNetworkOther?: string;
}

interface OverviewTabProps {
  accountsId: string;
  account: {
    companyName: string;
    dba: string | null;
    lifecyclePhase: LifecyclePhase;
    subStage: AccountsSubStage;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
    state: string | null;
    estimatedFtEmployees: number | null;
    estimatedPtEmployees?: number | null;
    estimatedStartDate: Date | string | null;
    group: {
      id: string;
      estimatedMemberCount: number | null;
      groupType?: string | null;
      metadata?: GroupCoverageMetadata | null;
    } | null;
  };
  /** RFC internal-use coverage selections — internal Plan Year & Coverage card. */
  rfcInternal?: RfcCoverageInternalData | null;
  /** Total employee count from the RFC group-info form (FT + PT). */
  rfcTotalEmployees?: number | null;
  /** Primary contact (contacts are returned primary-first) — internal
   *  Identity card's Primary Contact sub-block. */
  primaryContact?: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    role: string | null;
  } | null;
  /** Opens the proposal generator (switches to the Proposals tab). */
  onGenerateProposal?: () => void;
  /** Hide the Proposals card (basic internal detail view has no Proposals tab). */
  showProposals?: boolean;
  /** Read-only virtual VBA account — no summary editing (and no lock banner,
   *  which would misread as a CBS handoff for a group that was always live). */
  readOnly?: boolean;
}

function formatDate(d: Date | string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

// Display labels for the RFC internal-use enums (mirrors the select options in
// ClientRfcReview's Internal Use Only section).
const RFC_CARRIER_LABELS: Record<string, string> = {
  AMERICAN_GLOBAL: 'American Global',
  CLEAR_SPRING_HEALTH: 'Clear Spring Health',
  SELF_FUNDED: 'Self Funded',
};
const RFC_TELEMEDICINE_LABELS: Record<string, string> = {CLEVER_HEALTH: 'Clever Health'};
const RFC_PBM_LABELS: Record<string, string> = {C3RX: 'C3RX', VERUS: 'Verus'};
const RFC_APP_LABELS: Record<string, string> = {CLEVER: 'Clever'};
const RFC_MEC_LABELS: Record<string, string> = {
  CHAMPION_HEALTH: 'Champion Health MEC',
  CHAMPION_HEALTH_HSA: 'Champion Health HSA MEC',
  HEALTHCUES: 'HealthCues MEC',
  QUANTUM: 'Quantum MEC',
};
const RFC_PPO_NETWORK_LABELS: Record<string, string> = {FIRST_HEALTH: 'First Health', PHCS: 'PHCS'};

/** Resolve an RFC internal enum selection to display text. 'OTHER' resolves to
 *  the freeform companion field; an unfilled 'OTHER' counts as no value. */
function rfcChoiceLabel(
  value: string | undefined,
  otherText: string | undefined,
  labels: Record<string, string>,
): string | null {
  if (!value) return null;
  if (value === 'OTHER') return otherText?.trim() || null;
  return labels[value] ?? value;
}

/** Humanize the Group.groupType enum (SELF_BILL → "Self Bill"); the
 *  UNKNOWN/NOT_SURE placeholders render as no value. */
function groupTypeLabel(groupType: string | null | undefined): string | null {
  if (!groupType || groupType === 'UNKNOWN' || groupType === 'NOT_SURE') return null;
  return groupType
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

type SummaryEditForm = {
  companyName: string;
  dba: string;
  members: string;
  state: UsStateCode | '';
  estimatedStartDate: string;
};

function toDateInputValue(d: Date | string | null): string {
  return d ? format(new Date(d), 'yyyy-MM-dd') : '';
}

export function OverviewTab({
  accountsId,
  account,
  primaryContact = null,
  rfcInternal = null,
  rfcTotalEmployees = null,
  onGenerateProposal,
  showProposals = true,
  readOnly = false,
}: OverviewTabProps): React.ReactElement {
  const members = account.group?.estimatedMemberCount ?? account.estimatedFtEmployees;
  const config = useAccountsViewConfig();
  const utils = trpc.useUtils();
  const isInternal = useIsInternal();

  // Summary editing is admin-only (brokers cannot edit it). Client-portal
  // admins lose the pencil once the account is sent to CBS; internal staff
  // keep it — they own the record after the hand-off. Distinct from the
  // broader canEdit rule that gates non-summary affordances.
  const user = useAuthStore((s) => s.user);
  const permissions = usePermissions();
  // Mirrors the server-side gate on `account.updateCompanyDetails`:
  // cross-tenant internal users, or tenant users holding `accounts.admin`
  // (isAccountsAdmin) plus the `accounts.edit_basic` action key.
  const isAdmin = user?.cbsInternalProfile
    ? permissions.has(CODE_PERMISSIONS.tenant.accessAll.key)
    : !!user?.clientProfile &&
      permissions.has(CODE_PERMISSIONS.accounts.admin.key) &&
      permissions.has(CODE_PERMISSIONS.accounts.editBasic.key);
  const sentToCbs = isAccountSentToCbs(account.subStage);
  const summaryLocked = sentToCbs && !isInternal;
  const canEditSummary = !readOnly && isAdmin && !summaryLocked;

  const [editing, setEditing] = React.useState(false);
  const [membersError, setMembersError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<SummaryEditForm>({
    companyName: '',
    dba: '',
    members: '',
    state: '',
    estimatedStartDate: '',
  });

  const updateSummary = trpc.account.updateCompanyDetails.useMutation({
    onSuccess: () => {
      utils.account.getById.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
      utils.account.list.invalidate();
      setEditing(false);
    },
  });

  function startEdit(): void {
    setForm({
      companyName: account.companyName,
      dba: account.dba ?? '',
      members: members !== null ? String(members) : '',
      state: (account.state as UsStateCode | null) ?? '',
      estimatedStartDate: toDateInputValue(account.estimatedStartDate),
    });
    updateSummary.reset();
    setMembersError(null);
    setEditing(true);
  }

  function saveEdit(): void {
    const payload: RouterInput['account']['updateCompanyDetails'] = {accountsId};

    // The account name can be corrected but never blanked — a cleared value
    // falls back to the current name (by omitting it from the update).
    const name = form.companyName.trim();
    if (name && name !== account.companyName) payload.companyName = name;

    const dba = form.dba.trim() || null;
    if (dba !== (account.dba ?? null)) payload.dba = dba;

    // Number() (not parseInt) so fractional/exponent input is rejected rather
    // than silently truncated; invalid values surface a local error and never
    // reach the payload.
    const membersRaw = form.members.trim();
    let memberCount: number | null = null;
    if (membersRaw !== '') {
      const parsed = Number(membersRaw);
      if (!Number.isInteger(parsed) || parsed < 0) {
        setMembersError('Members must be a whole number of 0 or more.');
        return;
      }
      memberCount = parsed;
    }
    setMembersError(null);
    if (memberCount !== members) payload.memberCount = memberCount;

    if (form.state && form.state !== account.state) payload.state = form.state;

    if (form.estimatedStartDate !== toDateInputValue(account.estimatedStartDate)) {
      payload.estimatedStartDate = form.estimatedStartDate
        ? new Date(`${form.estimatedStartDate}T00:00:00`)
        : null;
    }

    if (Object.keys(payload).length === 1) {
      setEditing(false);
      return;
    }
    updateSummary.mutate(payload);
  }
  const position = resolveAccountPosition(config, account);
  const phaseLabel = position?.stage.label ?? PHASE_LABEL[account.lifecyclePhase];
  // Show the milestone being worked toward (same node as the lifecycle band),
  // labeled through the active Accounts-view config. Mirrors the Accounts table
  // / detail header badge exactly: advance to the active milestone, but don't
  // advance onto a milestone this tenant's pipeline omits — fall back to the
  // account's own represented sub-stage so the label matches the config.
  const activeSubStage = toActiveSubStage(account.subStage);
  const activePosition =
    activeSubStage === account.subStage
      ? position
      : findPositionBySystemSubStage(config, activeSubStage);
  const displaySubStage = activePosition ? activeSubStage : account.subStage;
  const displayPosition = activePosition ?? position;
  const subStageLabel = displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage];
  const proposals = trpc.account.getProposals.useQuery({accountsId}, {enabled: showProposals});
  const fission = trpc.account.getFissionUrl.useQuery(
    {accountsId},
    {enabled: !!accountsId && showProposals, refetchOnWindowFocus: false},
  );
  const hasProposals = (proposals.data?.length ?? 0) > 0;
  // Proposal generation is only available when the client has a Fission URL
  // configured. Surface it as an error state on the Overview so the gap is
  // visible without digging into the Proposals tab.
  const proposalNotConfigured = !fission.isLoading && !fission.error && !fission.data?.url;

  // Edit inputs shared by both the internal ProfileCard layout and the
  // client-portal grid layout.
  const companyNameInput = (
    <Input
      value={form.companyName}
      onChange={(e) => setForm((f) => ({...f, companyName: e.target.value}))}
      placeholder={account.companyName}
    />
  );
  const dbaInput = (
    <Input value={form.dba} onChange={(e) => setForm((f) => ({...f, dba: e.target.value}))} />
  );
  const membersInput = (
    <>
      <Input
        type='number'
        min='0'
        value={form.members}
        aria-invalid={membersError ? true : undefined}
        onChange={(e) => {
          setMembersError(null);
          setForm((f) => ({...f, members: e.target.value}));
        }}
      />
      {membersError ? <p className='mt-1 text-xs text-red-600'>{membersError}</p> : null}
    </>
  );
  const stateInput = (
    <StateSelect
      value={form.state}
      onValueChange={(v) => setForm((f) => ({...f, state: v as UsStateCode}))}
      className='w-full'
    />
  );
  const startDateInput = (
    <Input
      type='date'
      value={form.estimatedStartDate}
      onChange={(e) => setForm((f) => ({...f, estimatedStartDate: e.target.value}))}
    />
  );

  const editButton =
    canEditSummary && !editing ? (
      <Button
        variant='ghost'
        size='sm'
        className='-mr-2 h-6 px-2'
        onClick={startEdit}
        title='Edit account summary'
      >
        <Pencil className='h-3 w-3' />
      </Button>
    ) : null;

  const lockedBanner =
    !readOnly && isAdmin && summaryLocked ? (
      <div className='mb-5 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900'>
        <Lock className='mt-0.5 h-5 w-5 shrink-0 text-blue-600' />
        <div className='min-w-0'>
          <p className='text-sm font-semibold'>Account Summary is locked</p>
          <p className='mt-0.5 text-sm text-blue-800'>
            This account has been sent to CBS for implementation. CBS owns the record from this
            point, so the summary can no longer be edited.
          </p>
        </div>
      </div>
    ) : null;

  const saveBar = editing ? (
    <div className='mt-1 space-y-2'>
      {updateSummary.error ? (
        <div className='text-sm text-red-600'>{updateSummary.error.message}</div>
      ) : null}
      <div className='flex gap-2'>
        <Button size='sm' onClick={saveEdit} disabled={updateSummary.isPending}>
          {updateSummary.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size='sm'
          variant='outline'
          onClick={() => setEditing(false)}
          disabled={updateSummary.isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  ) : null;

  const contactFullName = primaryContact
    ? [primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(' ').trim() || null
    : null;

  const phaseTooltip = (variant: 'phase' | 'subStage'): React.ReactElement => (
    <StageTooltip
      phase={account.lifecyclePhase}
      subStage={account.subStage}
      viewStageKey={account.viewStageKey}
      viewSubStageKey={account.viewSubStageKey}
      variant={variant}
    />
  );

  // Plan Year & Coverage sources, cbs-portal-style: the RFC's internal-use
  // selections are authoritative pre-group; the linked Group's metadata (set by
  // the CBS build) fills in once it exists. Missing values render as an em-dash.
  const meta = account.group?.metadata ?? null;
  const anticipatedDate = meta?.planYearAnticipatedDate ?? account.estimatedStartDate;
  const oeRange = meta?.openEnrollmentStartDate
    ? `${formatDate(meta.openEnrollmentStartDate)} → ${
        meta.openEnrollmentEndDate ? formatDate(meta.openEnrollmentEndDate) : '—'
      }`
    : null;
  const carrierValue =
    rfcChoiceLabel(rfcInternal?.carrierName, rfcInternal?.carrierNameOther, RFC_CARRIER_LABELS) ??
    meta?.carrier;
  const ppoNetworkValue =
    rfcChoiceLabel(rfcInternal?.ppoNetwork, rfcInternal?.ppoNetworkOther, RFC_PPO_NETWORK_LABELS) ??
    meta?.ppoNetwork;
  const pbmValue =
    rfcChoiceLabel(rfcInternal?.pbm, rfcInternal?.pbmOther, RFC_PBM_LABELS) ?? meta?.pbm;
  const appEnrollerValue =
    rfcChoiceLabel(rfcInternal?.app, rfcInternal?.appOther, RFC_APP_LABELS) ?? meta?.enroller;
  const telemedicineValue = rfcChoiceLabel(
    rfcInternal?.telemedicine,
    rfcInternal?.telemedicineOther,
    RFC_TELEMEDICINE_LABELS,
  );
  const mecProviderValue = rfcChoiceLabel(rfcInternal?.mec, rfcInternal?.mecOther, RFC_MEC_LABELS);
  // Total FT+PT employees — MSP sizing input. The RFC group-info count wins;
  // the lead-capture FT/PT estimates are the pre-RFC fallback.
  const estimatedTotalEmployees =
    account.estimatedFtEmployees !== null || (account.estimatedPtEmployees ?? null) !== null
      ? (account.estimatedFtEmployees ?? 0) + (account.estimatedPtEmployees ?? 0)
      : null;
  const totalEmployees = rfcTotalEmployees ?? estimatedTotalEmployees;

  return (
    <div className='space-y-5'>
      {isInternal ? (
        /* cbs-portal Overview: stacked ProfileCards — Identity (with Primary
           Contact sub-block) then Plan Year & Coverage. */
        <>
          {lockedBanner}
          <ProfileCard title='Identity' action={editButton}>
            <ProfileRow
              label='Company Name'
              value={editing ? companyNameInput : account.companyName}
            />
            <ProfileRow label='DBA' value={editing ? dbaInput : account.dba} />
            <ProfileRow label='State' value={editing ? stateInput : account.state} />
            <ProfileSubheading>Primary Contact</ProfileSubheading>
            <ProfileRow label='Name' value={contactFullName} />
            <ProfileRow label='Email' value={primaryContact?.email} />
            <ProfileRow label='Phone' value={primaryContact?.phone} />
            <ProfileRow label='Role' value={primaryContact?.role} />
          </ProfileCard>
          <ProfileCard title='Plan Year & Coverage'>
            <ProfileRow
              label='Phase'
              value={
                <span className='inline-flex items-center gap-1'>
                  {phaseLabel}
                  {phaseTooltip('phase')}
                </span>
              }
            />
            <ProfileRow
              label='Sub-stage'
              value={
                <span className='inline-flex items-center gap-1'>
                  {subStageLabel}
                  {phaseTooltip('subStage')}
                </span>
              }
            />
            <ProfileRow
              label='Plan Year Start'
              value={meta?.planYearStartDate ? formatDate(meta.planYearStartDate) : null}
            />
            <ProfileRow
              label='Plan Year End'
              value={meta?.planYearEndDate ? formatDate(meta.planYearEndDate) : null}
            />
            {/* The account's editable expected-effective date doubles as the
                anticipated date until the CBS build stamps one on the group —
                once the group date exists it wins the display, so editing the
                account date would look like a no-op; show it read-only then. */}
            <ProfileRow
              label='Anticipated Date'
              value={
                editing && !meta?.planYearAnticipatedDate
                  ? startDateInput
                  : anticipatedDate
                    ? formatDate(anticipatedDate)
                    : null
              }
            />
            <ProfileRow label='Open Enrollment' value={oeRange} />
            <ProfileRow label='PPO Network' value={ppoNetworkValue} />
            <ProfileRow label='PBM' value={pbmValue} />
            <ProfileRow label='Carrier' value={carrierValue} />
            <ProfileRow label='App / Enroller' value={appEnrollerValue} />
            <ProfileRow label='Telemedicine' value={telemedicineValue} />
            <ProfileRow label='MEC Provider' value={mecProviderValue} />
            <ProfileRow label='Group Type' value={groupTypeLabel(account.group?.groupType)} />
            {/* No ERISA-structure source in Infinity yet — renders an em-dash
                until the field lands (tracked on the RFC/build side). */}
            <ProfileRow label='ERISA Structure' value={null} />
            <ProfileRow
              label='Members'
              value={editing ? membersInput : members !== null ? String(members) : null}
            />
            <ProfileRow
              label='Total Employees'
              value={totalEmployees !== null ? String(totalEmployees) : null}
            />
          </ProfileCard>
          {saveBar}
        </>
      ) : (
        <Card className='border-border p-6 shadow-sm'>
          <div className='mb-5 flex items-center justify-between'>
            <h3 className='text-sm font-semibold uppercase tracking-wide text-foreground'>
              Account Summary
            </h3>
            {editButton}
          </div>

          {lockedBanner}

          <dl className='grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-3'>
            {editing ? (
              <EditableField label='Company Name'>{companyNameInput}</EditableField>
            ) : (
              <Field label='Company Name' value={account.companyName} />
            )}
            {editing ? (
              <EditableField label='DBA'>{dbaInput}</EditableField>
            ) : (
              <Field label='DBA' value={account.dba ?? '—'} />
            )}
            {editing ? (
              <EditableField label='Members'>{membersInput}</EditableField>
            ) : (
              <Field label='Members' value={members !== null ? String(members) : '—'} />
            )}
            {editing ? (
              <EditableField label='State'>{stateInput}</EditableField>
            ) : (
              <Field label='State' value={account.state ?? '—'} />
            )}
            <Field label='Phase' value={phaseLabel} info={phaseTooltip('phase')} />
            <Field label='Sub-stage' value={subStageLabel} info={phaseTooltip('subStage')} />
            {editing ? (
              <EditableField label='Expected Effective'>{startDateInput}</EditableField>
            ) : (
              <Field label='Expected Effective' value={formatDate(account.estimatedStartDate)} />
            )}
          </dl>

          {editing ? <div className='mt-5'>{saveBar}</div> : null}
        </Card>
      )}

      {showProposals ? (
        <Card className='border-border p-6 shadow-sm'>
          <h3 className='mb-5 text-sm font-semibold uppercase tracking-wide text-foreground'>
            Proposals
          </h3>
          {hasProposals ? (
            <div className='space-y-2'>
              {proposals.data?.map((p) => (
                <ProposalRow
                  key={p.id}
                  proposal={p}
                  companyName={account.companyName}
                  collapsible
                />
              ))}
            </div>
          ) : proposalNotConfigured ? (
            <div className='flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900'>
              <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-amber-600' />
              <div className='min-w-0'>
                <p className='text-sm font-semibold'>Proposal generation not configured</p>
                <p className='mt-0.5 text-sm text-amber-800'>
                  Proposal generation is not set up for this client. Contact your administrator to
                  enable it.
                </p>
              </div>
            </div>
          ) : (
            <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-10 text-center'>
              <FileText className='h-6 w-6 text-muted-foreground' />
              <p className='text-sm font-medium text-foreground'>No proposals yet.</p>
              <p className='text-xs text-muted-foreground'>Generate one from the Proposals tab.</p>
              {onGenerateProposal ? (
                <Button type='button' size='sm' className='mt-2' onClick={onGenerateProposal}>
                  <Wand2 className='w-4 h-4 mr-1.5' />
                  Generate proposal
                </Button>
              ) : null}
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}

function EditableField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className='min-w-0'>
      <dt className='text-xs font-medium text-muted-foreground'>{label}</dt>
      <dd className='mt-1'>{children}</dd>
    </div>
  );
}

function Field({
  label,
  value,
  info,
}: {
  label: string;
  value: React.ReactNode;
  info?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className='min-w-0'>
      <dt className='flex items-center gap-1 text-xs font-medium text-muted-foreground'>
        {label}
        {info}
      </dt>
      <dd className='mt-1 truncate text-base font-semibold text-foreground'>{value}</dd>
    </div>
  );
}
